import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { recipes } from '../data/recipes';
import type { Recipe } from '../data/types';
import { FILTERS, GROUPS, labelFor, type FilterGroup } from '../data/filters';
import { RecipeCard } from '../components/RecipeCard';
import { useStars } from '../lib/stars';

type Meal = 'all' | Recipe['meal'];

// Search, meal, starred — plus the filter groups from src/data/filters.ts.
// Only values that at least one recipe carries get a chip, so the rows stay
// short until the library grows into them. Selections live in the URL
// (?category=meal&cuisines=italian,mexican) so a filtered view can be sent
// to someone as a link.

function valuesOf(r: Recipe, g: FilterGroup): string[] {
  return g === 'category' ? [r.category] : r[g];
}

function Chip({
  active,
  onClick,
  children,
  title,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      title={title}
      className={`h-10 rounded-full border px-3.5 font-sans text-[0.82rem] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink ${
        active ? 'border-ink bg-ink text-paper' : 'border-line bg-card text-ink-soft hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') ?? '');
  const meal = (params.get('meal') ?? 'all') as Meal;
  const starredOnly = params.get('starred') === '1';
  const { stars } = useStars();
  const [openGroup, setOpenGroup] = useState<FilterGroup | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // A dropdown closes on outside tap or Escape, like a menu should.
  useEffect(() => {
    if (!openGroup) return;
    function onDown(e: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenGroup(null);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenGroup(null);
    }
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openGroup]);

  const selected = useMemo(() => {
    const out = {} as Record<FilterGroup, string[]>;
    for (const g of GROUPS) {
      const raw = params.get(g);
      out[g] = raw ? raw.split(',').filter((v) => v in FILTERS[g].values) : [];
    }
    return out;
  }, [params]);

  function update(fn: (p: URLSearchParams) => void) {
    const next = new URLSearchParams(params);
    fn(next);
    setParams(next, { replace: true });
  }
  function toggle(g: FilterGroup, key: string) {
    update((p) => {
      const cur = selected[g];
      const next = FILTERS[g].single
        ? cur.includes(key) ? [] : [key]
        : cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
      if (next.length) p.set(g, next.join(','));
      else p.delete(g);
    });
  }
  function clearAll() {
    update((p) => {
      for (const g of GROUPS) p.delete(g);
      p.delete('meal');
      p.delete('starred');
    });
    setQuery('');
  }

  // Which chips to offer: only values some recipe actually has.
  const available = useMemo(() => {
    const out = {} as Record<FilterGroup, string[]>;
    for (const g of GROUPS) {
      const keys = Object.keys(FILTERS[g].values);
      out[g] = keys.filter((k) => recipes.some((r) => valuesOf(r, g).includes(k)));
    }
    return out;
  }, []);

  const anyFilter = GROUPS.some((g) => selected[g].length) || meal !== 'all' || starredOnly || query.trim();

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = recipes.filter((r) => {
      if (meal !== 'all' && r.meal !== meal) return false;
      if (starredOnly && !stars[r.slug]) return false;
      for (const g of GROUPS) {
        const want = selected[g];
        if (want.length && !want.some((k) => valuesOf(r, g).includes(k))) return false;
      }
      if (q) {
        const words = GROUPS.flatMap((g) => valuesOf(r, g).flatMap((k) => [k, labelFor(g, k)]));
        const hay = [r.title, r.summary, ...words, ...r.ingredients.map((i) => i.name)].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return [...list].reverse(); // registry order = order added; newest first
  }, [query, meal, starredOnly, stars, selected]);

  const nothingAtAll = recipes.length === 0;

  return (
    <div className="mx-auto max-w-5xl px-4 pb-16 sm:px-6">
      <div className="pt-8 pb-6 sm:pt-12">
        <h1 className="font-serif text-4xl font-semibold tracking-tight sm:text-5xl">
          The house cookbook
        </h1>
        <p className="mt-2 font-serif text-lg text-ink-soft">
          Batch recipes for the freezer — pick one, cook it, leave a note on how it went.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <label className="relative block">
          <span className="sr-only">Search recipes</span>
          <svg
            viewBox="0 0 24 24"
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.2-3.2" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, cuisine, type, or ingredient…"
            className="h-11 w-full rounded-full border border-line bg-card pl-10 pr-4 font-sans text-[0.95rem] placeholder:text-ink-soft/70 focus:border-ink-soft focus:outline-none"
          />
        </label>

        <div className="flex flex-wrap items-center gap-2">
          {(['all', 'breakfast', 'lunch', 'dinner'] as Meal[]).map((m) => (
            <Chip
              key={m}
              active={meal === m}
              onClick={() => update((p) => (m === 'all' ? p.delete('meal') : p.set('meal', m)))}
            >
              <span className="capitalize">{m === 'all' ? 'All meals' : m}</span>
            </Chip>
          ))}
          <span aria-hidden="true" className="mx-1 h-6 w-px bg-line" />
          <Chip
            active={starredOnly}
            onClick={() => update((p) => (starredOnly ? p.delete('starred') : p.set('starred', '1')))}
            title="Show only starred recipes"
          >
            <span aria-hidden="true">★</span>
            <span className="sr-only sm:not-sr-only sm:ml-1.5">Starred</span>
          </Chip>
          {anyFilter && (
            <button
              type="button"
              onClick={clearAll}
              className="ml-auto h-10 rounded-full px-3 font-sans text-[0.82rem] font-medium text-ink-soft underline decoration-dotted underline-offset-4 hover:text-ink"
            >
              Clear all
            </button>
          )}
        </div>

        <div ref={menuRef}>
          <div className="flex flex-wrap items-center gap-2">
            {GROUPS.filter((g) => available[g].length > 0).map((g) => {
              const picked = selected[g];
              const open = openGroup === g;
              const label = picked.length
                ? `${FILTERS[g].label} · ${picked.map((k) => labelFor(g, k)).join(', ')}`
                : FILTERS[g].label;
              return (
                <button
                  key={g}
                  type="button"
                  aria-expanded={open}
                  aria-controls="filter-panel"
                  onClick={() => setOpenGroup(open ? null : g)}
                  className={`inline-flex h-10 max-w-full items-center gap-1.5 rounded-full border px-3.5 font-sans text-[0.82rem] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink ${
                    picked.length ? 'border-ink bg-ink text-paper' : open ? 'border-ink-soft bg-card text-ink' : 'border-line bg-card text-ink-soft hover:text-ink'
                  }`}
                >
                  <span className="truncate">{label}</span>
                  <svg viewBox="0 0 24 24" className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
              );
            })}
          </div>
          {/* One panel for whichever group is open, in normal flow so it can
              never hang off the edge of a phone; it slides open by height. */}
          <div
            id="filter-panel"
            className={`grid transition-[grid-template-rows] duration-200 ease-out ${openGroup ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
          >
            <div className="overflow-hidden">
              {openGroup && (
                <div
                  role="group"
                  aria-label={FILTERS[openGroup].label}
                  className="mt-2 flex flex-wrap gap-2 rounded-xl border border-line bg-card p-3"
                >
                  {available[openGroup].map((k) => (
                    <Chip key={k} active={selected[openGroup].includes(k)} onClick={() => toggle(openGroup, k)}>
                      {labelFor(openGroup, k)}
                    </Chip>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {results.length > 0 ? (
        <ul className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {results.map((r) => (
            <li key={r.slug}>
              <RecipeCard recipe={r} />
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-16 text-center">
          <p className="font-serif text-xl text-ink">
            {nothingAtAll
              ? 'The cookbook is empty — the first recipes are on their way.'
              : starredOnly && !query
                ? 'No stars yet.'
                : 'Nothing matches that.'}
          </p>
          <p className="mt-1.5 font-serif text-ink-soft">
            {nothingAtAll
              ? 'Check back in a day or two.'
              : starredOnly && !query
                ? 'Tap the star on any recipe and it will wait for you here.'
                : 'Loosen a filter, or search for an ingredient — “soy”, “lentils”, “tortillas”.'}
          </p>
        </div>
      )}
    </div>
  );
}
